import { Link } from 'react-router-dom';
import { forwardRef, type KeyboardEvent, MouseEvent, type ReactNode, useEffect, useRef } from 'react';

import clsx from 'clsx';
import { Icon } from '../icon';
import { Divider } from '../divider';
import { Shortcut } from '../shortcut';

import type { AlertProps } from '../alert';

import styles from './menu-item.module.scss';

export const MenuItem = forwardRef((props: MenuItemProps, forwardedRef) => {
  const {
    icon,
    name,
    link,
    ready,
    danger,
    onClick,
    selected,
    shortcut,
    disabled,
    renderer,
    className,
    closeMenu,
    deleteData,
    highlighted,
    type = 'item',
    headerAction,
    rightNode,
    hasSelection,
    multiColorIcon,
    showConfirmation,
    customViewTitle,
    ...rest
  } = props;

  const localRef = useRef<HTMLElement>(null);

  const ref = (node: HTMLElement | null) => {
    if (forwardedRef) {
      if (typeof forwardedRef === 'function') {
        forwardedRef(node);
      } else {
        forwardedRef.current = node;
      }
    }
    localRef.current = node;
  };

  useEffect(() => {
    if (highlighted) {
      localRef.current?.scrollIntoView({ block: 'nearest' });
    }
  }, [highlighted]);

  const renderNonMenuOption = () => {
    switch (type) {
      case 'divider':
        return (
          <div className={clsx('divider-holder', className)}>
            <Divider size="100%" orientation="horizontal" />
          </div>
        );
      case 'group':
        return (
          <div className={styles.menuGroupHolder}>
            <span>{name}</span>
          </div>
        );
      default:
        return null;
    }
  };

  if (['divider', 'group'].includes(type)) {
    return renderNonMenuOption();
  }

  // A disabled item is inert for every activation path: CSS only blocks the pointer, so the
  // click and Enter handlers have to opt out on their own.
  const handleEnterKey = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && !disabled) {
      onClick?.();
    }
  };

  const rootClassName = clsx(styles.menuItem, className, {
    [styles.danger]: danger,
    [styles.disabled]: disabled,
    [styles.selected]: selected,
    [styles.highlighted]: highlighted,
    [styles.header]: type === 'header',
  });

  const content =
    type === 'item' && renderer ? (
      renderer({ ready, showConfirmation, deleteData })
    ) : (
      <ul role="menuitem">
        <li>
          {!!icon && <Icon type={icon} multiColor={multiColorIcon} className={styles.icon} size={16} />}
          <span className={clsx('ellipsis-text', styles.name)}>{name}</span>
        </li>
        <li>
          {!!shortcut && <Shortcut data={shortcut} className={styles.shortcut} />}
          {hasSelection && <Icon type="tick-02" size={16} className={styles.tickHolder} />}
          {rightNode ?? (type === 'header' ? headerAction : null)}
        </li>
      </ul>
    );

  if (link && !disabled) {
    return (
      <Link
        to={link}
        ref={ref}
        tabIndex={0}
        draggable={false}
        onClick={closeMenu}
        className={rootClassName}
        aria-selected={selected}
        onKeyDown={handleEnterKey}
        {...rest}
      >
        {content}
      </Link>
    );
  }

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={disabled ? -1 : 0}
      data-testid="menu-item-button"
      className={rootClassName}
      aria-selected={selected}
      aria-disabled={disabled}
      onClick={(e) => {
        if (!disabled) {
          onClick?.(e);
        }
        e.preventDefault();
        e.stopPropagation();
      }}
      onKeyDown={handleEnterKey}
      {...rest}
    >
      {content}
    </div>
  );
});

export interface MenuItemProps {
  name?: string;
  icon?: string;
  type?: string;
  link?: string;
  ready?: boolean;
  danger?: boolean;
  shortcut?: string;
  selected?: boolean;
  className?: string;
  disabled?: boolean;
  highlighted?: boolean;
  closeMenu?: () => void;
  hasSelection?: boolean;
  deleteData?: AlertProps;
  headerAction?: ReactNode;
  rightNode?: ReactNode;
  customViewTitle?: string;
  multiColorIcon?: boolean;
  showConfirmation?: boolean;
  onClick?: (e?: MouseEvent) => void;
  renderer?: (props: any) => ReactNode;
}
